import axios, { type InternalAxiosRequestConfig } from "axios";
import {
  getAccessToken,
  getRefreshToken,
  clearTokens,
  setTokens,
} from "%/token";
import { getAppContextHeaders } from "%/appVersion";
import { businessRefreshToken, userRefreshToken } from "~/services/auth";

export const privateHttp = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

export const publicHttp = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

const applyAppContextHeaders = (config: InternalAxiosRequestConfig) => {
  const headers = getAppContextHeaders();

  Object.entries(headers).forEach(([key, value]) => {
    config.headers[key] = value;
  });

  return config;
};

// 토큰 설정 함수 (기존 호환성 유지)
export const setAuthToken = (token: string | null) => {
  if (token) {
    privateHttp.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete privateHttp.defaults.headers.common["Authorization"];
  }
};

// privateHttp Request Interceptor - 자동으로 토큰 헤더 추가
privateHttp.interceptors.request.use(
  (config) => {
    applyAppContextHeaders(config);

    const accessToken = getAccessToken();

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// privateHttp Response Interceptor - 토큰 자동 갱신 포함
privateHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    console.log("API Error:", error.response);
    console.log("error.response?.status:", error.response?.status);
    // 401 Unauthorized 또는 403 Forbidden 모두 처리
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      const {
        isTokenRefreshing,
        setTokenRefreshing,
        addToFailedQueue,
        processQueue,
      } = await import("%/tokenRefresh");

      if (isTokenRefreshing()) {
        // 이미 토큰 갱신 중이면 큐에 추가하고 대기
        return new Promise((resolve, reject) => {
          addToFailedQueue(
            (token: string | null) => {
              if (token) {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(privateHttp(originalRequest));
              } else {
                reject(error);
              }
            },
            (err: Error | undefined) => {
              reject(err || error);
            }
          );
        });
      }

      originalRequest._retry = true;
      setTokenRefreshing(true);

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          // 리프레시 토큰이 없으면 로그아웃 - 현재 화면 유지하고 에러 반환
          console.warn("No refresh token available");
          setTokenRefreshing(false);
          processQueue(new Error("No refresh token"), null);
          clearTokens();
          // window.location.href = "/"; // 제거: 현재 화면 유지
          return Promise.reject(error);
        }

        // 토큰 갱신 시도 - 앱 타입에 따라 적절한 함수 선택
        console.log("Attempting token refresh...");
        const isBusinessApp = import.meta.env.VITE_APP_TYPE === "business";
        const response = isBusinessApp
          ? await businessRefreshToken({ refreshToken })
          : await userRefreshToken({ refreshToken });

        console.log("Token refresh response:", response);

        if (response.statusCode === 200 && response.data?.accessToken) {
          // 갱신된 토큰 저장 및 헤더 업데이트
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken;

          // 토큰 저장
          setTokens(newAccessToken, newRefreshToken);

          // axios 기본 헤더 업데이트
          setAuthToken(newAccessToken);

          // 원래 요청 헤더 업데이트
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          setTokenRefreshing(false);
          processQueue(null, newAccessToken);

          console.log("Token refresh successful, retrying original request");
          return privateHttp(originalRequest);
        } else {
          // 토큰 갱신 실패 - 현재 화면 유지하고 원래 에러 반환
          console.error("Token refresh failed with response:", response);
          setTokenRefreshing(false);
          processQueue(new Error("Token refresh failed"), null);
          clearTokens();
          // window.location.href = "/"; // 제거: 현재 화면 유지
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // 리프레시 토큰도 만료되었거나 갱신 실패 - 현재 화면 유지
        console.error("Token refresh error:", refreshError);
        setTokenRefreshing(false);
        processQueue(refreshError as Error, null);
        clearTokens();
        // window.location.href = "/"; // 제거: 현재 화면 유지
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// publicHttp Request Interceptor - 앱 컨텍스트 헤더 추가
publicHttp.interceptors.request.use(
  (config) => {
    return applyAppContextHeaders(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// publicHttp Response Interceptor
publicHttp.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log("Public API Error:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    });
    return Promise.reject(error);
  }
);

// 초기 토큰 설정 (페이지 로드 시)
const initializeAuth = () => {
  const accessToken = getAccessToken();
  if (accessToken) {
    setAuthToken(accessToken);
  }
};
export interface CommonQueryProps {
  [key: string]: any | undefined | null;
}
export const querystring = (query: CommonQueryProps) => {
  return {
    params: query,
  };
};

// 페이지 로드 시 토큰 초기화
initializeAuth();
